import React, { useEffect, useState } from 'react';
import * as yup from 'yup';
import Checkbox from '../../../Components/Base/Checkbox/Checkbox';
import DiagnosisSelectField from '../../../Components/Base/Diagnosis/DiagnosisSelectField';
import TextField from '../../../Components/Base/TextField/TextField';
import SubmitButton from '../../../Components/Button/SubmitButton';
import NumberField from '../../../Components/Form/components/NumberField';
import Select from '../../../Components/Form/components/Select';
import FormControl from '../../../Components/Form/FormControl';
import { PrescriptionType } from '../../../Enums/PrescriptionType.enum';
import useTranslation from '../../../Hooks/useTranslation';
import PrescriptionMedicamentsSelect, {
	PrescriptionMedicamentsSelectValue,
} from '../../Prescription/Components/PrescriptionMedicamentsSelect';
import { PrescriptionMedicament } from '../../../graphql/graphql';
import EPrescriptionMedicamentWarnings from './EPrescriptionMedicamentWarnings';
import EPrescriptionMedicamentLegend from './EPrescriptionMedicamentLegend';
import FormWrapper from '../../../Components/Form/FormWrapper';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { v4 as uuidv4 } from 'uuid';
import useNotification from '../../../Hooks/useNotification.hook';
import { getPaymentOptions } from '../Services/eRecept.service';
import DosageField from './DosageField/DosageField';
import InputWrapper from '../../../Components/Form/components/InputWrapper/InputWrapper';
import PrescriptionMedicamentsSelectField from '../../Prescription/Components/PrescriptionMedicamentsSelectField';
import SelectField from '../../../Components/Form/components/SelectField';

export interface EPrescriptionHVLPMedicament {
	id: string;
	formType: PrescriptionType;
	item: PrescriptionMedicament;
	quantity: number;
	payment?: {
		label: string;
		value: string;
	};
	insuranceType?: {
		label: string;
		value: string;
	};
	manual: string;
	requestNumber?: number;
	doNotConfuse?: boolean;
	overrun?: boolean;
	diagnosis?: {
		label: string;
		value: string;
	};
	associatedDiagnosis?: {
		label: string;
		value: string;
	};
}

interface EPrescriptionMedicamentsHVLPFormProps {
	onSubmit?: (values: EPrescriptionHVLPMedicament) => void;
	defaultValues?: Record<string, any>;
	patientId?: string;
	isSelfPayer: boolean;
	submitButtonText?: string;
}

const EPrescriptionMedicamentsHVLPForm: React.FC<
	EPrescriptionMedicamentsHVLPFormProps
> = ({ onSubmit, defaultValues, isSelfPayer, submitButtonText }) => {
	const { t } = useTranslation();
	const { showNotificationAlert } = useNotification();

	const validationSchema = yup.object({
		item: yup.object().required('validation.ePrescription.item'),
		quantity: yup
			.number()
			.required('')
			.typeError('validation.ePrescription.number'),
		payment: yup
			.object({
				label: yup.string(),
				value: yup.string().required('Úhrada je povinná'),
			})
			.required(''),
		insuranceType: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
		manual: yup
			.string()
			.required(t('validation.ePrescriptionMedicamentsHVLPForm.dosage')),
		requestNumber: yup
			.number()
			.notRequired()
			.nullable()
			.transform(value => (value ? value : null))
			.typeError('validation.ePrescription.number'),
		doNotConfuse: yup.bool().notRequired(),
		overrun: yup.bool().notRequired(),
		diagnosis: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
		associatedDiagnosis: yup
			.object({
				label: yup.string(),
				value: yup.string(),
			})
			.nullable(),
	});

	const formMethods = useForm({
		defaultValues: defaultValues || {},
		resolver: yupResolver(validationSchema),
	});

	const values = formMethods.watch ? formMethods.watch() : {};

	useEffect(() => {
		if (['F', 'P', 'V'].includes(values?.item?.item?.dispensing)) {
			formMethods?.setValue?.('payment', {
				label: t('terms.suklUhrada.pacient') ?? '',
				value: 'PACIENT',
			});
		} else {
			formMethods?.setValue?.('payment', defaultValues?.payment ?? undefined);
		}
	}, [values?.item?.item?.dispensing, formMethods?.setValue]);

	useEffect(() => {
		if (defaultValues) {
			setSelectedMedicament(defaultValues.item);
			formMethods?.setValue?.('item', {
				id: defaultValues.item.id,
				label: defaultValues.item.name,
			});
		}
	}, [defaultValues]);

	const [selectedMedicament, setSelectedMedicament] =
		useState<PrescriptionMedicament>();
	const handleSave = (values: any) => {
		const payment = values.payment ?? defaultValues?.payment ?? null;
		const data: EPrescriptionHVLPMedicament = {
			id: defaultValues?.id ?? uuidv4(),
			formType: PrescriptionType.HVLP,
			item: values.item.item ?? defaultValues?.item,
			quantity: values.quantity,
			payment,
			insuranceType: values.insuranceType ?? null,
			manual: values.manual,
			requestNumber: values.requestNumber ?? null,
			doNotConfuse: values.doNotConfuse,
			overrun: values.overrun,
			diagnosis: values.diagnosis ?? null,
			associatedDiagnosis: values.associatedDiagnosis ?? null,
		};
		onSubmit?.(data);
	};

	const pickMedicament = (medicament: PrescriptionMedicamentsSelectValue) => {
		setSelectedMedicament(medicament.item);
	};

	const paymentOptions = getPaymentOptions(isSelfPayer);

	return (
		<FormWrapper formMethods={formMethods}>
			<EPrescriptionMedicamentLegend />
			<PrescriptionMedicamentsSelectField
				label='component.ePrescription.nameOfMeds'
				name='item'
				isRequired
				isWrapped
				onChange={pickMedicament}
			/>
			{selectedMedicament && (
				<div className={'mb-3'}>
					<EPrescriptionMedicamentWarnings item={selectedMedicament} />
				</div>
			)}
			<NumberField
				label='component.ePrescription.amount'
				name='quantity'
				isRequired
			/>
			<SelectField
				name='payment'
				label='component.ePrescription.payment'
				options={paymentOptions}
				isRequired
				isWrapped
			/>
			<InputWrapper>
				<DosageField
					label='component.ePrescription.dosage'
					name='manual'
					isRequired
				/>
			</InputWrapper>
			<NumberField
				label='component.ePrescription.applicationNumber'
				name='requestNumber'
			/>
			<div className='row'>
				<div className='col-md-6'>
					<FormControl
						label={'component.ePrescription.notToBeMistaken'}
						name='doNotConfuse'>
						<Checkbox />
					</FormControl>
				</div>
				<div className='col-md-6'>
					<FormControl
						label={'component.ePrescription.overStep'}
						name='overrun'>
						<Checkbox />
					</FormControl>
				</div>
			</div>
			<FormControl label='base.shared.diagnosis' name='diagnosis'>
				<DiagnosisSelectField name='diagnosis' portal={false} />
			</FormControl>
			<FormControl
				label={'component.ePrescription.additionalDiagnosis'}
				name='associatedDiagnosis'>
				<DiagnosisSelectField name='associatedDiagnosis' portal={false} />
			</FormControl>
			<div className='d-flex mt-4 justify-content-between'>
				<SubmitButton
					title={submitButtonText ?? 'base.shared.continue'}
					onSubmit={handleSave}
				/>
			</div>
		</FormWrapper>
	);
};

export default EPrescriptionMedicamentsHVLPForm;
